import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { PropertyValues } from 'lit';
import { AccordionGroupController, type AccordionGroupHost } from './accordion-group.controller';
import type { HeadingLevel, PanelType } from '../../elements/asc-accordion-panel/asc-accordion-panel';

/**
 * Orchestration container for one or more `asc-accordion-panel` elements.
 *
 * Controls two panel-level behaviours from a single attribute:
 * 1. **Exclusive vs. multiple-open mode** — `multiple` (default `false`) ensures
 *    only one panel is open at a time; set `multiple` to allow several at once.
 * 2. **Uniform visual style** — `panel-type` propagates `container` or `line`
 *    styling to every slotted panel automatically, so individual panels never
 *    need their `type` attribute set directly.
 *
 * Keyboard navigation (Down/Up Arrow, Home, End) between panel triggers is
 * handled automatically per the WAI-ARIA Accordion pattern.
 *
 * ## Usage
 *
 * ```html
 * <asc-accordion-group>
 *   <asc-accordion-panel>
 *     <span slot="header">Question one</span>
 *     <p>Answer one.</p>
 *   </asc-accordion-panel>
 *   <asc-accordion-panel>
 *     <span slot="header">Question two</span>
 *     <p>Answer two.</p>
 *   </asc-accordion-panel>
 * </asc-accordion-group>
 * ```
 *
 * ## Events
 *
 * | Event | Description |
 * |---|---|
 * | `asc-change` | Fired after any open-state change, including cascading closes. Detail: `{ target, open, openPanels }` |
 *
 * ## CSS Custom Properties
 *
 * | Property | Default | Description |
 * |---|---|---|
 * | `--asc-accordion-group-gap` | `var(--asc-spacing-md)` | Gap between panels (container type only) |
 *
 * @element asc-accordion-group
 *
 * @fires asc-change - Fired after any open-state change, including exclusive-mode cascading closes.
 *   Detail: `{ target: HTMLElement; open: boolean; openPanels: HTMLElement[] }`
 *
 * @slot - Default slot. Accepts `asc-accordion-panel` elements. Non-panel children are ignored.
 *
 * @csspart base - Root `<div>` — flex column layout container. Primary hook for max-width, margin, and layout overrides.
 *
 * @cssprop --asc-accordion-group-gap - Vertical gap between panels (container type only). Default: `var(--asc-spacing-md)`
 */
@customElement('asc-accordion-group')
export class AscAccordionGroup extends LitElement implements AccordionGroupHost {
  static override styles = css`
    :host {
      display: block;
    }

    .base {
      display: flex;
      flex-direction: column;
      gap: var(--asc-accordion-group-gap, var(--asc-spacing-md));
    }

    /* Line-type panels stack flush — no gap prevents doubled borders. */
    :host([panel-type='line']) .base {
      gap: 0;
    }
  `;

  private readonly _controller = new AccordionGroupController(this);

  /** When false (default), only one panel may be open at a time. When true, multiple panels may be open simultaneously. */
  @property({ type: Boolean, reflect: true }) multiple = false;

  /**
   * Visual style applied uniformly to all slotted `asc-accordion-panel` children.
   * `container` (default) — full border, border-radius, and drop shadow.
   * `line` — bottom border only, no radius or shadow, panels stack flush.
   * Reflected so `:host([panel-type="line"])` CSS removes the inter-panel gap.
   */
  @property({ type: String, reflect: true, attribute: 'panel-type' })
  panelType: PanelType = 'container';

  /**
   * ARIA heading level propagated to every child panel's `role="heading"` wrapper.
   * Set to match the document heading outline at the accordion's location.
   */
  @property({ type: Number, attribute: 'heading-level' })
  headingLevel: HeadingLevel = 3;

  private _handleSlotChange(): void {
    this._controller.syncPanels();
  }

  override updated(changed: PropertyValues<this>): void {
    if (changed.has('panelType') || changed.has('headingLevel')) {
      this._controller.syncPanels();
    }
  }

  override render() {
    return html`
      <div part="base" class="base">
        <slot @slotchange=${this._handleSlotChange}></slot>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-accordion-group': AscAccordionGroup;
  }
}
